import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../services/api';

const DEFAULT_DISCOUNT_PERCENT = 40;

const calcDiscountPrice = (price, percent) => {
  const p = parseFloat(price);
  if (!Number.isFinite(p) || p <= 0) return '';
  const discounted = Math.round(p * (1 - percent / 100));
  return String(discounted);
};

const calcSavingsPercent = (original, sale) => {
  const o = parseFloat(original);
  const s = parseFloat(sale);
  if (!o || o <= 0) return 0;
  return Math.round(((o - s) / o) * 100);
};

// Read variant.option{1,2,3} in the order of product.options[].name.
// Returns { [optionName]: optionValue }.
const variantOptionMap = (product, variant) => {
  if (!product || !variant) return {};
  const map = {};
  (product.options || []).forEach((opt, idx) => {
    const v = variant[`option${idx + 1}`];
    if (opt && opt.name && v) map[opt.name] = v;
  });
  return map;
};

const findMatchingVariant = (product, selectedOptions) => {
  if (!product) return null;
  const variants = product.variants || [];
  const opts = product.options || [];
  if (opts.length === 0) return variants[0] || null;
  return (
    variants.find((v) =>
      opts.every((opt, idx) => {
        const expected = selectedOptions[opt.name];
        const actual = v[`option${idx + 1}`];
        return expected ? actual === expected : true;
      })
    ) || null
  );
};

const buildVariantDetails = (variant) => {
  const opts = [variant.option1, variant.option2, variant.option3].filter(Boolean);
  return opts.length ? opts.join(' / ') : 'Default';
};

// Build a fully-formed outlet payload (plus a `key` for cart bookkeeping)
// from a product + chosen variant + outlet price + quantity.
const buildOutletItem = (product, variant, discountPrice, quantity) => {
  const variantDetails = buildVariantDetails(variant);
  const baseSku = variant.sku || `${product.handle}-${variant.id}`;
  const displaySku = variantDetails !== 'Default'
    ? `${baseSku} (${variantDetails})`
    : baseSku;

  const images = (product.images || []).map((img, i) => ({
    id: img.id || i + 1,
    src: img.src,
    position: img.position || i + 1,
  }));

  return {
    key: variant.id, // unique per variant; used to dedupe within the cart
    source: 'storefront',
    variant_id: variant.id,
    sku: baseSku,
    title: product.title,
    variant_title: variant.title || variantDetails,
    variant_details: variantDetails,
    display_sku: displaySku,
    description: product.description || null,
    image_url: images[0]?.src || null,
    all_images: images.length ? JSON.stringify(images) : null,
    price: parseFloat(variant.price) || 0,
    discount_price: parseFloat(discountPrice),
    available_quantity: parseInt(quantity, 10) || 1,
    slot_number: '', // cart-only bin override; '' = let the server auto-assign
  };
};

// Predict the next bin the server would auto-assign, given current occupancy,
// pool config, and any bins already staged in the cart. Mirrors the backend
// _pickOutletSlot (compact: first bin under target; else least-loaded).
const pickNextSlot = (stats, stagedBins) => {
  if (!stats || !stats.binCount) return '';
  const load = { ...(stats.occupancy || {}) };
  (stagedBins || []).forEach((b) => { if (b) load[b] = (load[b] || 0) + 1; });
  let leastBin = 1;
  let leastLoad = Infinity;
  for (let bin = 1; bin <= stats.binCount; bin += 1) {
    const l = load[bin] || 0;
    if (l < stats.binTarget) return bin;
    if (l < leastLoad) { leastLoad = l; leastBin = bin; }
  }
  return leastBin;
};

const StorefrontProductSearch = ({ onProductSelect, onMessageShow }) => {
  const [tag, setTag] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [discountPrice, setDiscountPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState([]); // staged outlet items, committed together
  const [committing, setCommitting] = useState(false);
  const [includeUnlisted, setIncludeUnlisted] = useState(false);
  // Product image gallery (click a card's image to browse all photos)
  const [gallery, setGallery] = useState({ open: false, images: [], index: 0, title: '' });
  // Waiting orders (storefront + shopee) that need the selected variant — lets
  // the operator allocate a spare to the order instead of listing it on outlet.
  const [pendingMatches, setPendingMatches] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [fulfillQty, setFulfillQty] = useState(1);
  const [fulfilling, setFulfilling] = useState(false);
  // Result of the last allocate — carries the receiving bin so the operator
  // knows where to physically place each item right after allocating.
  const [lastFulfilled, setLastFulfilled] = useState([]);
  // Bin occupancy + pool config, used to prefill each cart line's bin number.
  const [slotStats, setSlotStats] = useState(null);
  const [syncingShopee, setSyncingShopee] = useState(false);
  // The bin the selected variant ALREADY occupies on outlet (if any). When set,
  // new units of this variant are pinned to that same bin so the physical lot
  // isn't split across bins / re-pinned (the bin-drift bug).
  const [existingBin, setExistingBin] = useState(null);

  const selectedVariant = useMemo(
    () => findMatchingVariant(selectedProduct, selectedOptions),
    [selectedProduct, selectedOptions]
  );

  const variantInCart = useMemo(
    () => !!selectedVariant && cart.some((c) => c.key === selectedVariant.id),
    [selectedVariant, cart]
  );

  // When the matching variant changes, prefill outlet price from its base price.
  useEffect(() => {
    if (selectedVariant) {
      setDiscountPrice(calcDiscountPrice(selectedVariant.price, DEFAULT_DISCOUNT_PERCENT));
    } else {
      setDiscountPrice('');
    }
  }, [selectedVariant?.id]);

  // Look up waiting orders (storefront + shopee) that need the selected variant.
  useEffect(() => {
    if (!selectedVariant || !selectedProduct) {
      setPendingMatches([]);
      return;
    }
    let cancelled = false;
    const variantDetails = buildVariantDetails(selectedVariant);
    (async () => {
      try {
        setPendingLoading(true);
        const resp = await api.get('/api/outlet/admin/variant-pending-matches', {
          params: {
            variant_id: selectedVariant.id,
            sku: selectedVariant.sku || '',
            variant_details: variantDetails,
            title: selectedProduct.title || '',
            limit: 20,
          },
        });
        if (!cancelled) {
          setPendingMatches(resp.data?.matches || []);
          setFulfillQty(1);
        }
      } catch (error) {
        if (!cancelled) setPendingMatches([]);
      } finally {
        if (!cancelled) setPendingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedVariant?.id, selectedProduct?.id]);

  // Look up the bin this variant already occupies on outlet, so we pin new units
  // to the SAME physical bin instead of re-pinning the whole lot.
  useEffect(() => {
    if (!selectedVariant) { setExistingBin(null); return; }
    let cancelled = false;
    const variantDetails = buildVariantDetails(selectedVariant);
    (async () => {
      try {
        const resp = await api.get('/api/outlet/admin/variant-bin', {
          params: { sku: selectedVariant.sku || '', variant_details: variantDetails },
        });
        if (!cancelled) setExistingBin(resp.data?.existing?.slot_number ?? null);
      } catch (error) {
        if (!cancelled) setExistingBin(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedVariant?.id]);

  const showMessage = (msg, type = 'success') => {
    if (typeof onMessageShow === 'function') onMessageShow(msg, type);
  };

  // Refresh current bin occupancy so cart prefills reflect the latest state.
  const refreshSlotStats = async () => {
    try {
      const resp = await api.get('/api/outlet/admin/slots/stats');
      setSlotStats(resp.data || null);
    } catch (error) {
      /* non-fatal — bin field just falls back to auto (empty) */
    }
  };
  useEffect(() => { refreshSlotStats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const resetSelection = () => {
    setSelectedProduct(null);
    setSelectedOptions({});
    setDiscountPrice('');
    setQuantity(1);
  };

  const handleSearch = async () => {
    const q = tag.trim();
    if (!q) {
      showMessage('Please enter a tag or keyword', 'warning');
      return;
    }

    try {
      setSearchLoading(true);
      resetSelection();

      // Primary: OMS metadata tag (same source as ProductsPage / ReceivingQueuePage).
      // Joins product_metadata.tag → variants.sku → storefront_products.
      const metaRes = await api.get('/api/storefront/products/by-metadata-tag', {
        params: { tag: q, limit: 60, ...(includeUnlisted ? { includeUnlisted: 'true' } : {}) },
      });
      let list = metaRes.data?.products || [];

      // Fallback: fuzzy search across title/description/storefront-tags.
      if (list.length === 0) {
        const searchRes = await api.get('/api/storefront/search', {
          params: { q, limit: 60, ...(includeUnlisted ? { includeUnlisted: 'true' } : {}) },
        });
        list = searchRes.data?.products || [];
      }

      setProducts(list);
      if (list.length === 0) {
        showMessage('No storefront products found for this tag', 'info');
      }
    } catch (error) {
      console.error('Storefront search error:', error);
      showMessage(error.response?.data?.message || 'Search failed', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    // Toggle: clicking the already-selected product closes its panel.
    if (selectedProduct?.id === product.id) {
      resetSelection();
      return;
    }
    setSelectedProduct(product);
    // Default each option dropdown to the first variant's value.
    const firstVariant = (product.variants || []).find((v) => v.available !== false)
      || (product.variants || [])[0]
      || null;
    setSelectedOptions(variantOptionMap(product, firstVariant));
    setQuantity(1);
  };

  const handleOptionChange = (optionName, value) => {
    setSelectedOptions((prev) => ({ ...prev, [optionName]: value }));
  };

  // Stage the currently-selected variant into the cart (no API call yet).
  // Re-adding the same variant replaces its price/quantity with the latest.
  const handleAddToCart = () => {
    if (!selectedProduct || !selectedVariant) {
      showMessage('Select a product and variant first', 'warning');
      return;
    }
    const priceNum = parseFloat(discountPrice);
    const originalNum = parseFloat(selectedVariant.price);
    if (!priceNum || priceNum <= 0) {
      showMessage('Enter a valid outlet price', 'warning');
      return;
    }
    if (priceNum >= originalNum) {
      showMessage('Outlet price must be lower than original price', 'warning');
      return;
    }
    if (!quantity || quantity < 1) {
      showMessage('Quantity must be at least 1', 'warning');
      return;
    }

    const item = buildOutletItem(selectedProduct, selectedVariant, priceNum, quantity);
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.key === item.key);
      if (idx >= 0) {
        const next = [...prev];
        // Re-adding: keep the operator's existing bin choice.
        next[idx] = { ...item, slot_number: prev[idx].slot_number };
        return next;
      }
      // If this variant already lives in a bin, DEFAULT the field to that bin as a
      // hint (so re-stocking keeps it together) — but leave it fully editable so
      // the operator can place the new units in whatever bin they choose.
      if (existingBin != null) {
        item.slot_number = String(existingBin);
        return [...prev, item];
      }
      // New line: prefill the bin the server would assign, counting bins already
      // staged in the cart so multiple adds pack compactly (7, 7, 8…).
      const stagedBins = prev.map((c) => parseInt(c.slot_number, 10)).filter(Boolean);
      const nextBin = pickNextSlot(slotStats, stagedBins);
      item.slot_number = nextBin ? String(nextBin) : '';
      return [...prev, item];
    });
    showMessage(`Added ${item.display_sku} to cart`, 'success');
  };

  const removeFromCart = (key) => {
    setCart((prev) => prev.filter((c) => c.key !== key));
  };

  const updateCartQty = (key, value) => {
    const qty = Math.max(1, parseInt(value, 10) || 1);
    setCart((prev) => prev.map((c) => (c.key === key ? { ...c, available_quantity: qty } : c)));
  };

  // Bin override per cart line. Empty = auto-assign on the server; a number
  // pins the item to that bin after it's added.
  const updateCartSlot = (key, value) => {
    const clean = value === '' ? '' : String(Math.max(1, parseInt(value, 10) || 1));
    setCart((prev) => prev.map((c) => (c.key === key ? { ...c, slot_number: clean } : c)));
  };

  const clearCart = () => setCart([]);

  // Open the image gallery for a product (all its photos, not just the cover).
  const openGallery = (product, e) => {
    if (e) e.stopPropagation();
    const imgs = (product.images || []).map((im) => im.src).filter(Boolean);
    if (imgs.length === 0) return;
    setGallery({ open: true, images: imgs, index: 0, title: product.title || '' });
  };
  const closeGallery = () => setGallery((g) => ({ ...g, open: false }));
  const navGallery = (dir) =>
    setGallery((g) => ({ ...g, index: (g.index + dir + g.images.length) % g.images.length }));

  // Commit every staged item to the outlet, sequentially (respects rate limits).
  // Successful items are removed; failures stay in the cart for retry.
  const handleAddAllToOutlet = async () => {
    if (cart.length === 0) {
      showMessage('Cart is empty', 'warning');
      return;
    }

    setCommitting(true);
    let success = 0;
    const failed = [];

    for (const item of cart) {
      // slot_number is cart-only bookkeeping: the add endpoint auto-assigns a
      // bin, and a non-empty override is applied afterwards via the admin slot
      // endpoint (so an existing item can be re-pinned too).
      const { key, slot_number, bin_locked, ...payload } = item;
      try {
        const response = await api.post('/api/outlet/add', payload);
        const created = response.data?.item;
        const binOverride = (slot_number ?? '').toString().trim();
        // Full manual control: whatever bin the operator typed always wins, whether
        // this created a new lot or merged into an existing one. The operator places
        // the physical stock, so the typed bin is the source of truth. (A blank field
        // leaves the server's auto-assigned bin untouched.)
        if (created?.id && binOverride !== '') {
          try {
            await api.patch(`/api/outlet/admin/items/${created.id}/slot`, {
              slot: parseInt(binOverride, 10),
            });
          } catch (slotErr) {
            console.warn('Bin override failed:', slotErr);
          }
        }
        success += 1;
        if (typeof onProductSelect === 'function') {
          onProductSelect(created || payload);
        }
      } catch (error) {
        console.error('Add storefront item error:', error);
        failed.push({ item, error });
      }
    }

    setCommitting(false);
    refreshSlotStats(); // occupancy changed — keep later prefills accurate

    if (failed.length === 0) {
      showMessage(`Added ${success} item${success === 1 ? '' : 's'} to outlet`, 'success');
      setCart([]);
      resetSelection();
    } else {
      setCart(failed.map((f) => f.item)); // keep only the ones that failed
      const has429 = failed.some((f) => f.error.response?.status === 429);
      showMessage(
        `Added ${success}, ${failed.length} failed${has429 ? ' — rate limited, retry in a few seconds' : ''}`,
        success > 0 ? 'warning' : 'error'
      );
    }
  };

  // Allocate the physical spare to the oldest waiting order(s): mark received +
  // 備註 (storefront and/or shopee). Does NOT list the item on the outlet.
  const handleFulfillOrders = async () => {
    if (!selectedVariant || !selectedProduct || pendingMatches.length === 0) return;
    const variantDetails = buildVariantDetails(selectedVariant);
    const qty = Math.max(1, Math.min(pendingMatches.length, parseInt(fulfillQty, 10) || 1));
    try {
      setFulfilling(true);
      const resp = await api.post('/api/outlet/admin/variant-fulfill', {
        variant_id: selectedVariant.id,
        sku: selectedVariant.sku || '',
        variant_details: variantDetails,
        title: selectedProduct.title || '',
        quantity: qty,
      });
      const fulfilled = resp.data?.fulfilled || [];
      setLastFulfilled(fulfilled);
      showMessage(
        fulfilled.length
          ? `已撥給 ${fulfilled.length} 張訂單 — 請看下方要放到哪個收貨格`
          : '目前沒有可分配的訂單',
        fulfilled.length ? 'success' : 'info'
      );
      // Drop the filled orders from the list; refresh the outlet list's demand counts.
      const filledIds = new Set(fulfilled.map((f) => f.orderId));
      setPendingMatches((prev) => prev.filter((m) => !filledIds.has(m.order_id)));
      setFulfillQty(1);
      if (fulfilled.length && typeof onProductSelect === 'function') onProductSelect(null);
    } catch (error) {
      console.error('Fulfil order error:', error);
      showMessage(error.response?.data?.message || '分配失敗', 'error');
    } finally {
      setFulfilling(false);
    }
  };

  // Force-refresh Shopee active orders, then re-check the selected variant's
  // waiting orders with the fresh data.
  const handleSyncShopee = async () => {
    try {
      setSyncingShopee(true);
      await api.post('/api/outlet/admin/shopee/refresh');
      showMessage('已同步蝦皮訂單', 'success');
      if (selectedVariant && selectedProduct) {
        const variantDetails = buildVariantDetails(selectedVariant);
        try {
          const resp = await api.get('/api/outlet/admin/variant-pending-matches', {
            params: {
              variant_id: selectedVariant.id,
              sku: selectedVariant.sku || '',
              variant_details: variantDetails,
              title: selectedProduct.title || '',
              limit: 20,
            },
          });
          setPendingMatches(resp.data?.matches || []);
        } catch (e) { /* ignore */ }
      }
      if (typeof onProductSelect === 'function') onProductSelect(null);
    } catch (error) {
      showMessage(error.response?.data?.message || '同步蝦皮失敗', 'error');
    } finally {
      setSyncingShopee(false);
    }
  };

  const renderTagChips = (product) => {
    const tags = product.tags || [];
    if (tags.length === 0) return null;
    return (
      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {tags.slice(0, 6).map((t) => (
          <Chip
            key={t}
            label={t}
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); setTag(t); }}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>
    );
  };

  const savingsPercent = selectedVariant && discountPrice
    ? calcSavingsPercent(selectedVariant.price, discountPrice)
    : 0;

  // The variant + price + quantity editor, rendered inline directly beneath the
  // clicked product card (expanding-row pattern).
  const renderAddPanel = () => (
    <Paper elevation={3} sx={{ p: 3, border: '2px solid #3f51b5' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">
          Add to Cart — {selectedProduct.title}
        </Typography>
        {variantInCart && (
          <Chip
            icon={<CheckCircleIcon />}
            label="This variant is in the cart"
            color="success"
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      {(selectedProduct.variants || []).length > 0 ? (
        (selectedProduct.options || []).length > 0 ? (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Choose Variant</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {selectedProduct.options.map((option, idx) => {
                const optionKey = `option${idx + 1}`;
                // Available values for this option = distinct non-empty values across variants.
                const values = Array.from(
                  new Set(
                    (selectedProduct.variants || [])
                      .map((v) => v[optionKey])
                      .filter(Boolean)
                  )
                );
                return (
                  <Grid item xs={12} sm={6} md={4} key={option.name || optionKey}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={`option-${idx}-label`}>{option.name}</InputLabel>
                      <Select
                        labelId={`option-${idx}-label`}
                        label={option.name}
                        value={selectedOptions[option.name] || ''}
                        onChange={(e) => handleOptionChange(option.name, e.target.value)}
                      >
                        {values.map((val) => {
                          // OOS-mark values that have no available variant for the
                          // currently picked siblings (still selectable so user can
                          // explore combinations).
                          const tentative = { ...selectedOptions, [option.name]: val };
                          const match = findMatchingVariant(selectedProduct, tentative);
                          const oos = !match || match.available === false;
                          return (
                            <MenuItem key={val} value={val}>
                              {val}{oos ? ' (缺貨)' : ''}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                );
              })}
            </Grid>
            {!selectedVariant && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No variant matches this combination.
              </Alert>
            )}
            {selectedVariant && selectedVariant.available === false && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This variant is marked sold out on the storefront.
              </Alert>
            )}
          </>
        ) : null
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No variants on this product.
        </Alert>
      )}

      {selectedVariant && (pendingLoading || pendingMatches.length > 0) && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#fff8e1', borderColor: '#ffb74d' }}>
          {pendingLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">檢查待收訂單…</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                ⚠ 有 {pendingMatches.length} 張訂單在等這件 — 可直接撥給訂單（不上架出清）
              </Typography>
              <Box sx={{ maxHeight: 150, overflowY: 'auto', mb: 1 }}>
                {pendingMatches.map((m, idx) => {
                  const willFill = idx < (parseInt(fulfillQty, 10) || 1);
                  const d = m.order_date ? new Date(m.order_date).toLocaleDateString() : '';
                  return (
                    <Box
                      key={`${m.source}-${m.order_id}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: willFill ? 1 : 0.4 }}
                    >
                      <Chip
                        size="small"
                        label={willFill ? '✓ 收到' : '等待'}
                        color={willFill ? 'success' : 'default'}
                        variant={willFill ? 'filled' : 'outlined'}
                        sx={{ height: 18, fontSize: 11 }}
                      />
                      <Chip
                        size="small"
                        label={m.source === 'shopee' ? '蝦皮' : '官網'}
                        color={m.source === 'shopee' ? 'warning' : 'primary'}
                        variant="outlined"
                        sx={{ height: 18, fontSize: 11 }}
                      />
                      <Typography variant="caption">
                        {m.order_id}{d ? ` · ${d}` : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="撥給幾張"
                  type="number"
                  size="small"
                  value={fulfillQty}
                  onChange={(e) =>
                    setFulfillQty(Math.max(1, Math.min(pendingMatches.length, parseInt(e.target.value, 10) || 1)))
                  }
                  inputProps={{ min: 1, max: pendingMatches.length, style: { width: 64 } }}
                />
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={handleFulfillOrders}
                  disabled={fulfilling}
                  startIcon={fulfilling ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  撥給最舊 {Math.min(parseInt(fulfillQty, 10) || 1, pendingMatches.length)} 張（標記收到＋備註「出清調撥」）
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* After allocating: show where to physically put each item (receiving bin). */}
      {lastFulfilled.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#e8f5e9', borderColor: '#81c784' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              ✓ 已撥給訂單 — 請把商品放到：
            </Typography>
            <IconButton size="small" onClick={() => setLastFulfilled([])} title="關閉">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {lastFulfilled.map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
              <Chip
                size="small"
                label={f.source === 'shopee' ? '蝦皮' : '官網'}
                color={f.source === 'shopee' ? 'warning' : 'primary'}
                variant="outlined"
                sx={{ height: 18, fontSize: 11 }}
              />
              <Typography variant="body2">
                {f.orderId} →{' '}
                {f.receivingBin != null ? (
                  <b style={{ color: '#2e7d32' }}>收貨 {f.receivingBin} 號格</b>
                ) : (
                  <span style={{ color: '#777' }}>直接出貨（單件，不需上架）</span>
                )}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {selectedVariant && (
        <>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Original Price"
                type="number"
                fullWidth
                value={selectedVariant.price}
                disabled
                InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1 }}>$</Box> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={`Outlet Price (${DEFAULT_DISCOUNT_PERCENT}% off prefill)`}
                type="number"
                fullWidth
                value={discountPrice}
                onChange={(e) => setDiscountPrice(e.target.value)}
                InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1 }}>$</Box> }}
                error={!!discountPrice && (parseFloat(discountPrice) <= 0 || parseFloat(discountPrice) >= parseFloat(selectedVariant.price))}
                helperText={
                  discountPrice && parseFloat(discountPrice) >= parseFloat(selectedVariant.price)
                    ? 'Outlet price must be lower than original'
                    : discountPrice && parseFloat(discountPrice) <= 0
                      ? 'Price must be greater than zero'
                      : ''
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                inputProps={{ min: 1 }}
              />
            </Grid>

            {discountPrice && parseFloat(discountPrice) > 0 && parseFloat(discountPrice) < parseFloat(selectedVariant.price) && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }} variant="outlined">
                  <Typography variant="body2">
                    Savings: ${(parseFloat(selectedVariant.price) - parseFloat(discountPrice)).toFixed(2)} ({savingsPercent}% off)
                  </Typography>
                </Paper>
              </Grid>
            )}

            {existingBin != null && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  此款目前在出清 <strong>{existingBin} 號格</strong>（已預填），可沿用或改填其他格號。
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddShoppingCartIcon />}
                onClick={handleAddToCart}
                disabled={
                  !discountPrice ||
                  parseFloat(discountPrice) <= 0 ||
                  parseFloat(discountPrice) >= parseFloat(selectedVariant.price)
                }
              >
                {variantInCart ? 'Update in Cart' : 'Add to Cart'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                Stage this variant, pick another size/colour, then add them all to the outlet at once.
              </Typography>
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );

  // Staging cart — sticky so it stays visible while picking more variants.
  const renderCart = () => (
    <Paper
      elevation={4}
      sx={{
        p: 2,
        mb: 3,
        position: 'sticky',
        top: 8,
        zIndex: 5,
        border: '2px solid #2e7d32',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">
          Outlet Cart ({cart.length} variant{cart.length === 1 ? '' : 's'})
        </Typography>
        <Box>
          <Button
            size="small"
            color="inherit"
            onClick={clearCart}
            disabled={committing}
            sx={{ mr: 1 }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={committing ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            onClick={handleAddAllToOutlet}
            disabled={committing || cart.length === 0}
          >
            {committing ? 'Adding…' : `Add ${cart.length} to Outlet`}
          </Button>
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Variant</TableCell>
              <TableCell align="right">Original</TableCell>
              <TableCell align="right">Outlet</TableCell>
              <TableCell align="center">Qty</TableCell>
              <TableCell align="center">貨架格</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {cart.map((item) => (
              <TableRow key={item.key}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.title}
                        style={{ width: 36, height: 36, marginRight: 8, objectFit: 'contain' }}
                      />
                    )}
                    <Typography variant="body2" sx={{ maxWidth: 220 }} noWrap title={item.title}>
                      {item.title}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{item.variant_details}</TableCell>
                <TableCell align="right">${parseFloat(item.price).toFixed(0)}</TableCell>
                <TableCell align="right">${parseFloat(item.discount_price).toFixed(0)}</TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    value={item.available_quantity}
                    onChange={(e) => updateCartQty(item.key, e.target.value)}
                    inputProps={{ min: 1, style: { width: 56, textAlign: 'center' } }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    value={item.slot_number ?? ''}
                    onChange={(e) => updateCartSlot(item.key, e.target.value)}
                    placeholder="自動"
                    inputProps={{ min: 1, style: { width: 64, textAlign: 'center' } }}
                    title="留空 = 自動分配貨架格；輸入數字 = 指定格號"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeFromCart(item.key)}
                    disabled={committing}
                    title="Remove from cart"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <Box>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Search by tag or keyword"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            placeholder="e.g. 短袖, 落地褲, 韓製"
            variant="outlined"
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            disabled={searchLoading || !tag.trim()}
          >
            Search
          </Button>
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeUnlisted}
                onChange={(e) => setIncludeUnlisted(e.target.checked)}
              />
            }
            label="Include unlisted / sold-out"
          />
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={handleSyncShopee}
            startIcon={syncingShopee ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            disabled={syncingShopee}
            title="重新抓取蝦皮待出貨訂單，讓「等這件的訂單」偵測到最新訂單"
          >
            {syncingShopee ? '同步中…' : '同步蝦皮訂單'}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Tries exact tag first, falls back to fuzzy search across title/description/tags.
            {' '}Tick “Include unlisted / sold-out” to also find archived or unpublished products for clearance.
          </Typography>
        </Grid>
      </Grid>

      {cart.length > 0 && renderCart()}

      {products.length > 0 && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            {products.length} product{products.length === 1 ? '' : 's'} found
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {products.map((product) => {
              const cover = product.images?.[0]?.src;
              const priceMin = product.price_range?.min;
              const priceMax = product.price_range?.max;
              const isSelected = selectedProduct?.id === product.id;
              return (
                <React.Fragment key={product.id}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Paper
                      elevation={isSelected ? 4 : 1}
                      onClick={() => handleSelectProduct(product)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: isSelected ? '2px solid #3f51b5' : '1px solid #e0e0e0',
                        transition: 'all 0.2s',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {cover && (
                        <Box sx={{ textAlign: 'center', mb: 1, position: 'relative' }}>
                          <img
                            src={cover}
                            alt={product.title}
                            onClick={(e) => openGallery(product, e)}
                            title="點擊看更多圖片"
                            style={{ maxHeight: 140, maxWidth: '100%', objectFit: 'contain', cursor: 'zoom-in' }}
                          />
                          {(product.images?.length || 0) > 1 && (
                            <Chip
                              size="small"
                              label={`📷 ${product.images.length}`}
                              onClick={(e) => openGallery(product, e)}
                              sx={{
                                position: 'absolute', bottom: 0, right: 0, height: 20,
                                fontSize: '0.7rem', color: '#fff', bgcolor: 'rgba(0,0,0,0.6)',
                                cursor: 'pointer', '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                        </Box>
                      )}
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={product.title}>
                        {product.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ${priceMin}{priceMin !== priceMax && ` – $${priceMax}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(product.variants || []).length} variant{(product.variants || []).length === 1 ? '' : 's'}
                      </Typography>
                      {renderTagChips(product)}
                    </Paper>
                  </Grid>

                  {/* Inline editor: appears directly below the clicked card, not at the bottom of the grid. */}
                  {isSelected && (
                    <Grid item xs={12}>
                      {renderAddPanel()}
                    </Grid>
                  )}
                </React.Fragment>
              );
            })}
          </Grid>
        </>
      )}

      {/* Product image gallery — click a card image to browse all photos */}
      <Dialog open={gallery.open} onClose={closeGallery} maxWidth="md" fullWidth>
        <DialogContent sx={{ position: 'relative', bgcolor: '#111', p: 0 }}>
          <IconButton
            onClick={closeGallery}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360, position: 'relative' }}>
            {gallery.images.length > 1 && (
              <IconButton
                onClick={() => navGallery(-1)}
                sx={{ position: 'absolute', left: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
            <img
              src={gallery.images[gallery.index]}
              alt={gallery.title}
              style={{ maxHeight: '70vh', maxWidth: '100%', objectFit: 'contain' }}
            />
            {gallery.images.length > 1 && (
              <IconButton
                onClick={() => navGallery(1)}
                sx={{ position: 'absolute', right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}
          </Box>
          {gallery.images.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, p: 1, overflowX: 'auto', bgcolor: '#1a1a1a', justifyContent: 'center', flexWrap: 'wrap' }}>
              {gallery.images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  onClick={() => setGallery((g) => ({ ...g, index: i }))}
                  style={{
                    width: 48, height: 48, objectFit: 'cover', cursor: 'pointer', borderRadius: 4,
                    border: i === gallery.index ? '2px solid #90caf9' : '2px solid transparent',
                    opacity: i === gallery.index ? 1 : 0.6,
                  }}
                />
              ))}
            </Box>
          )}
          <Typography variant="caption" sx={{ color: '#bbb', display: 'block', textAlign: 'center', py: 0.5 }}>
            {gallery.title} · {gallery.index + 1} / {gallery.images.length}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StorefrontProductSearch;
